import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { EligibilityCalculator } from "@/components/EligibilityCalculator";
import { useRates } from "@/context/RatesContext";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { STATUSES } from "@/lib/constants";
import { ShieldCheck, Lock, Home, Building2, Briefcase, ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1565347878134-064b9185ced8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const PRODUCTS = [
  { icon: Home, type: "Home Loan", title: "Home Loans", desc: "Bespoke financing for premium residences, penthouses and villas up to ₹25 Cr." },
  { icon: Building2, type: "LAP", title: "Loan Against Property", desc: "Unlock liquidity from residential or commercial assets with flexible tenures." },
  { icon: Briefcase, type: "Business Loan", title: "Business Loans", desc: "Growth capital for established enterprises, structured around your cash flows." },
];

export default function Landing() {
  const { user } = useAuth();
  const { rates } = useRates();
  const cta = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register";

  return (
    <div className="min-h-screen gold-mesh text-white relative grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="rise text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]" data-testid="hero-eyebrow">Your Direct Selling Agent for Every Bank</p>
          <h1 className="rise delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="hero-title">
            One relationship. <span className="text-[#FF6B00]">Every bank</span> in India.
          </h1>
          <p className="rise delay-2 mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            Falcon Finserv is a Direct Selling Agent partnered with every leading bank, NBFC and housing finance company. We match your profile to the right lender, negotiate the best terms, handle documentation at your doorstep and track your file end-to-end — at zero cost to you.
          </p>
          <div className="rise delay-3 mt-10 flex flex-wrap gap-4">
            <Button asChild className="gold-btn rounded-full h-12 px-8 text-base">
              <Link to={cta} data-testid="hero-apply-button">
                {user ? "Open my portal" : "Begin your application"} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            {!user && (
              <Button asChild variant="outline" className="rounded-full h-12 px-8 text-base border-orange-500/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <a href="#calculator" data-testid="hero-calculator-button">Check eligibility</a>
              </Button>
            )}
          </div>
          <div className="rise delay-4 mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#FF6B00]" /> Partnered with all major banks &amp; NBFCs</span>
            <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#FF6B00]" /> Zero fee for borrowers</span>
            <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#FF6B00]" /> Doorstep documentation</span>
          </div>
        </div>
        <div className="lg:col-span-5 rise delay-2">
          <div className="relative rounded-2xl overflow-hidden border border-orange-500/20 shadow-2xl shadow-black/50">
            <img src={HERO_IMG} alt="Premium residence" className="h-[420px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070D1E] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 navy-glass rounded-xl p-5">
              <p className="text-xs uppercase tracking-wider text-[#FF6B00] font-semibold">Facilitated this quarter</p>
              <p className="mt-1 font-display text-3xl font-bold">₹412 Cr</p>
              <p className="text-sm text-slate-300">across 40+ partner lenders · one application, many offers</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 pb-20" id="calculator" data-testid="calculator-section">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Eligibility &amp; EMI</p>
        <h2 className="mt-3 mb-8 font-display text-base md:text-lg font-semibold text-slate-200">Know your number in seconds — then apply with one tap.</h2>
        <EligibilityCalculator />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 pb-20" data-testid="products-section">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">What we arrange</p>
        <h2 className="mt-3 font-display text-base md:text-lg font-semibold text-slate-200">Three products. Every partner bank compared. One relationship manager.</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-6 lg:gap-8">
          {PRODUCTS.map(({ icon: Icon, type, title, desc }, i) => (
            <div key={title} className={`rise delay-${i + 1} bg-white text-[#0F172A] rounded-2xl p-6 sm:p-8 shadow-xl shadow-black/30 hover:-translate-y-1 transition-transform`} data-testid={`product-card-${i}`}>
              <Icon className="h-8 w-8 text-[#FF6B00]" />
              <h3 className="mt-5 font-display text-xl font-semibold">{title}</h3>
              <p className="mt-1 text-sm font-semibold text-[#E05E00]" data-testid={`product-rate-${i}`}>from {rates[type].toFixed(2)}% p.a. across partner banks</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-orange-500/10 bg-[#070D1E]/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-16" data-testid="how-it-works-section">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">How a DSA works for you</p>
          <h2 className="mt-3 font-display text-base md:text-lg font-semibold text-slate-200">We sit between you and the bank — so you never have to chase one.</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {[
              ["01", "Profile & match", "We assess your income, property and goals, then shortlist the partner lenders most likely to approve — at the best rate."],
              ["02", "Negotiate & file", "Your relationship manager negotiates rate, fees and tenure, collects documents at your doorstep and logs the file with the bank."],
              ["03", "Sanction & disburse", "We follow up with the bank's credit, legal and valuation teams until sanction and disbursement — you track every step here."],
            ].map(([n, t, d]) => (
              <div key={n} className="navy-glass rounded-xl p-6">
                <span className="font-display text-2xl font-bold text-[#FF6B00]">{n}</span>
                <p className="mt-2 font-semibold text-slate-100">{t}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Partner network</p>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed" data-testid="partner-network">
            Public & private sector banks · Housing finance companies · NBFCs · Small finance banks — including SBI, HDFC Bank, ICICI Bank, Axis Bank, Kotak Mahindra Bank, LIC Housing Finance, Bajaj Finserv, Tata Capital and more.
          </p>
        </div>
      </section>

      <section className="border-t border-orange-500/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-16" data-testid="journey-section">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Your journey</p>
          <h2 className="mt-3 font-display text-base md:text-lg font-semibold text-slate-200">Six transparent stages with the partner bank — tracked live in your portal.</h2>
          <ol className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4">
            {STATUSES.map((s, i) => (
              <li key={s} className="navy-glass rounded-xl p-4">
                <span className="font-display text-2xl font-bold text-[#FF6B00]">0{i + 1}</span>
                <p className="mt-2 text-sm font-medium text-slate-100">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-8 text-xs text-slate-500 flex flex-wrap justify-between gap-3">
        <span>© {new Date().getFullYear()} Falcon Finserv · Direct Selling Agent for partner banks &amp; financial institutions</span>
        <span>Falcon Finserv does not lend on its own books. Sanction, rates and terms are decided by the partner lender.</span>
      </footer>
    </div>
  );
}
